import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import Ripple from '@/components/Ripple'
import CustomButton from '@/components/CustomButton'
import CurrencySelectPopup from '@/components/CurrencySelectPopup'
import { RecipientType, type RecentRecipient } from './index.type'
import { toFixedNoRound, formatThousands } from '@/utils'
import { useCurrencyList } from '@/hooks'
import type { CurrencyType } from '@/utils/userPreferences'
import { APIWeb } from '@/api'

export default function Transfer() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { currencyList } = useCurrencyList()

    // 币种选择
    const [selectedCurrency, setSelectedCurrency] = useState<CurrencyType>('USDT')
    const [amount, setAmount] = useState('')

    // 收款人信息
    const [recipientType, setRecipientType] = useState<RecipientType>(RecipientType.UID)
    const [recipientValue, setRecipientValue] = useState('')

    // 币种选择弹窗
    const [showCurrencyPopup, setShowCurrencyPopup] = useState(false)

    // 错误状态
    const [errors, setErrors] = useState<{
        amount?: string
        recipientValue?: string
    }>({})

    // 提交加载状态
    const [submitLoading, setSubmitLoading] = useState(false)

    // 当前选中的币种信息
    const selectedCoin = useMemo(() =>
        currencyList.find(c => c.name === selectedCurrency) || currencyList[0],
        [currencyList, selectedCurrency]
    )

    // 最近收款人列表（模拟数据，实际应从 API 获取）
    const recentRecipients = useMemo<RecentRecipient[]>(() => [
        // { id: '1', type: RecipientType.UID, value: 'UID123456', name: '张三', lastTransferTime: '2024-11-03' },
        // { id: '2', type: RecipientType.Email, value: 'test@example.com', name: '李四', lastTransferTime: '2024-11-02' }
    ], [])

    // 收款人类型选项
    const recipientTypes = useMemo(() => [
        { type: RecipientType.UID, label: 'UID' },
        { type: RecipientType.Email, label: t('邮箱') },
        // { type: RecipientType.Phone, label: t('手机号码') }
    ], [t])

    // 查看转账记录
    const handleViewRecord = useCallback(() => {
        navigate('/transfer/record')
    }, [navigate])

    // 打开币种选择弹窗
    const handleCoinSelect = useCallback(() => {
        setShowCurrencyPopup(true)
    }, [])

    // 处理币种选择
    const handleCurrencySelect = useCallback((currency: { symbol: string }) => {
        setSelectedCurrency(currency.symbol as CurrencyType)
        setAmount('')
    }, [])

    // 处理最大金额
    const handleMaxAmount = useCallback(() => {
        setAmount(toFixedNoRound(selectedCoin.balance, 2))
    }, [selectedCoin.balance])

    // 处理金额输入
    const handleAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        // 只允许数字和小数点
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setAmount(value)
            // 清除金额错误
            if (errors.amount) {
                setErrors(prev => ({ ...prev, amount: undefined }))
            }
        }
    }, [errors.amount])

    // 邮箱格式校验
    const isValidEmail = useCallback((email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }, [])

    // 手机号格式校验（支持国际格式）
    const isValidPhone = useCallback((phone: string) => {
        // 移除所有空格、横线、括号
        const cleanPhone = phone.replace(/[\s\-()]/g, '')
        // 检查是否为纯数字且长度在8-15位之间
        const phoneRegex = /^\+?\d{8,15}$/
        return phoneRegex.test(cleanPhone)
    }, [])

    // 处理收款人类型切换
    const handleRecipientTypeChange = useCallback((type: RecipientType) => {
        setRecipientType(type)
        setRecipientValue('')
        // 清除收款人错误
        setErrors(prev => ({ ...prev, recipientValue: undefined }))
    }, [])

    // 处理收款人信息输入
    const handleRecipientValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setRecipientValue(value)

        // 实时校验格式
        if (value.trim()) {
            switch (recipientType) {
                case RecipientType.Email:
                    if (!isValidEmail(value)) {
                        setErrors(prev => ({ ...prev, recipientValue: t('请输入有效的邮箱地址') }))
                    } else {
                        setErrors(prev => ({ ...prev, recipientValue: undefined }))
                    }
                    break
                case RecipientType.Phone:
                    if (!isValidPhone(value)) {
                        setErrors(prev => ({ ...prev, recipientValue: t('请输入有效的手机号码') }))
                    } else {
                        setErrors(prev => ({ ...prev, recipientValue: undefined }))
                    }
                    break
                case RecipientType.UID:
                    // UID 不需要实时校验，只要不为空即可
                    setErrors(prev => ({ ...prev, recipientValue: undefined }))
                    break
            }
        } else {
            // 清除错误
            setErrors(prev => ({ ...prev, recipientValue: undefined }))
        }
    }, [recipientType, isValidEmail, isValidPhone, t])

    // 检查表单是否有效
    const isFormValid = useMemo(() => {
        return (
            amount &&
            parseFloat(amount) > 0 &&
            parseFloat(amount) <= parseFloat(selectedCoin.balance) &&
            recipientValue.trim() !== ''
        )
    }, [amount, selectedCoin.balance, recipientValue])

    // 处理提交
    const handleSubmit = useCallback(async () => {
        const newErrors: { amount?: string; recipientValue?: string } = {}

        // 验证金额
        if (!amount || parseFloat(amount) <= 0) {
            newErrors.amount = t('请输入转账金额')
        } else if (parseFloat(amount) > parseFloat(selectedCoin.balance)) {
            newErrors.amount = t('余额不足')
        }

        // 验证收款人信息
        if (!recipientValue.trim()) {
            newErrors.recipientValue = t('请输入收款人信息')
        } else {
            // 根据类型校验格式
            switch (recipientType) {
                case RecipientType.Email:
                    if (!isValidEmail(recipientValue)) {
                        newErrors.recipientValue = t('请输入有效的邮箱地址')
                    }
                    break
                case RecipientType.Phone:
                    if (!isValidPhone(recipientValue)) {
                        newErrors.recipientValue = t('请输入有效的手机号码')
                    }
                    break
                case RecipientType.UID:
                    if (recipientValue.trim().length === 0) {
                        newErrors.recipientValue = t('请输入有效的UID')
                    }
                    break
            }
        }

        // 如果有错误，显示错误信息
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        // 调用转账目标账户校验接口
        try {
            setSubmitLoading(true)
            const result = await APIWeb.checkTransfer({
                target_user_type: recipientType,
                target_user_info: recipientValue.trim(),
            })

            if (!result) {
                setErrors({ recipientValue: t('无效的目标用户') })
                return
            }
        } catch {
            return
        } finally {
            setSubmitLoading(false)
        }

        // 跳转到确认页面
        navigate('/transfer/confirm', {
            state: {
                currency: selectedCurrency,
                currencyIcon: selectedCoin.icon,
                amount: amount,
                recipientType: recipientType,
                recipientValue: recipientValue,
                fee: '0' // 服务费，实际应从配置获取
            }
        })
    }, [amount, selectedCoin.balance, selectedCoin.icon, selectedCurrency, recipientType, recipientValue, isValidEmail, isValidPhone, navigate, t])

    // 获取收款人输入框 placeholder
    const getRecipientPlaceholder = useCallback(() => {
        switch (recipientType) {
            case RecipientType.UID:
                return 'UID'
            case RecipientType.Email:
                return t('请输入邮箱')
            case RecipientType.Phone:
                return t('请输入手机号')
            default:
                return ''
        }
    }, [recipientType, t])

    return (
        <PageLayout>
            <Header
                leftChildren={<Back />}
                centerChildren={<div className="text-3.75 font-medium text-black">{t('转账')}</div>}
                rightChildren={
                    <div className='flex items-center cursor-pointer' >
                        <div className='w-6 h-6' onClick={handleViewRecord}>
                            <img src="/icon/record-icon.svg" alt="record" />
                        </div>
                    </div>
                }
            />

            <div className="bg-white px-6 py-6">
                {/* 汇出 */}
                <div className="mb-4">
                    <div className="text-3.5 text-[#0F0F0F]/60 mb-3">{t('汇出')}</div>

                    {/* 币种选择和金额 */}
                    <div className={`flex min-h-16.5 items-center justify-between bg-white rounded-2xl p-4 border-[0.5px] border-solid ${errors.amount ? 'border-red-500' : 'border-[#E9E9E9]'}`}>
                        <div className="flex items-center flex-shrink-0" onClick={handleCoinSelect}>
                            <img src={selectedCoin.icon} alt={selectedCoin.name} className="w-5 h-5 mr-3" />
                            <div className="flex items-center">
                                <span className="text-base font-bold text-black mr-2">{selectedCoin.name}</span>
                                <img src="/icon/picker-down.svg" alt="" className="w-3" />
                            </div>
                        </div>

                        <div className="flex items-center justify-end flex-1 min-w-0">
                            <input
                                type="number"
                                value={amount}
                                onChange={handleAmountChange}
                                placeholder="0.00"
                                className="text-right text-5 font-semibold text-black placeholder-[#1A1A1A]/60 outline-none flex-1 min-w-0"
                            />
                            <Ripple className="ml-2 rounded-lg flex-shrink-0">
                                <div
                                    onClick={handleMaxAmount}
                                    className="px-3 py-1 bg-white text-3 rounded-lg border-[0.5px] border-solid border-[#E9E9E9]"
                                >
                                    {t('最大')}
                                </div>
                            </Ripple>
                        </div>
                    </div>

                    {/* 可用余额 */}
                    {!errors.amount && (
                        <div className="text-3 text-[#979797] mt-2">
                            {t('可用余额')}: <span className='text-black ml-2'>{formatThousands(selectedCoin.balance, 2, true)} {selectedCoin.name}</span>
                        </div>
                    )}
                </div>

                {/* 新增收款人 */}
                <div className="mb-6 border-0.5 border-solid border-[#E9E9E9] rounded-2xl p-4">
                    <div className="text-3.5 text-[#0F0F0F]/60 mb-3">{t('新增收款人')}</div>

                    {/* 收款人类型选择 */}
                    <div className="flex gap-2 mb-4 flex-wrap">
                        {recipientTypes.map(item => (
                            <Ripple className='rounded-full flex-shrink-0' key={item.type}>
                                <div
                                    onClick={() => handleRecipientTypeChange(item.type)}
                                    className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${recipientType === item.type
                                        ? 'bg-black text-white'
                                        : 'bg-[#F5F5F5] text-[#999999]'
                                        }`}
                                >
                                    {item.label}
                                </div>
                            </Ripple>
                        ))}
                    </div>

                    {/* 收款人信息输入 */}
                    <div className={`flex h-12 items-center bg-white border-[0.5px] border-solid rounded-full px-4 py-1 ${errors.recipientValue ? 'border-red-500' : 'border-[#E9E9E9]'}`}>
                        <input
                            type="text"
                            value={recipientValue}
                            onChange={handleRecipientValueChange}
                            placeholder={getRecipientPlaceholder()}
                            className="w-full text-base text-black outline-none placeholder-[#1A1A1A]/40"
                        />
                    </div>

                    {/* 错误提示 */}
                    {errors.recipientValue && (
                        <div className="text-red-500 text-xs mt-1">{errors.recipientValue}</div>
                    )}

                    {/* 提交按钮 */}
                    <div className="mt-6">
                        <CustomButton
                            onClick={handleSubmit}
                            disabled={!isFormValid}
                            loading={submitLoading}
                        >
                            {t('下一步')}
                        </CustomButton>
                    </div>
                </div>


                {/* <div className='bg-white border-[0.5px] border-solid border-[#E9E9E9] rounded-2xl p-4'>
                    最近收款人
                    <div className="text-3.5 text-[#0F0F0F]/60 mb-3">{t('最近收款人')}</div>

                    最近收款人列表
                    {recentRecipients.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <img src="/icon/no-address-book.svg" alt="empty" className="w-24 h-24 mb-4 opacity-30" />
                            <div className="text-sm text-[#999999]">{t('暂无收款人记录')}</div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {recentRecipients.map(recipient => (
                                <Ripple key={recipient.id}>
                                    <div className="bg-white rounded-2xl p-4 cursor-pointer">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-[#F5F5F5] flex items-center justify-center">
                                                    <span className="text-lg font-medium text-[#556AFF]">
                                                        {recipient.name?.[0] || recipient.value[0]}
                                                    </span>
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-black mb-1">
                                                        {recipient.name || recipient.value}
                                                    </div>
                                                    <div className="text-xs text-[#999999]">
                                                        {recipient.type.toUpperCase()}: {recipient.value}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-xs text-[#999999]">
                                                {recipient.lastTransferTime}
                                            </div>
                                        </div>
                                    </div>
                                </Ripple>
                            ))}
                        </div>
                    )}
                </div> */}
            </div>

            {/* 币种选择 Popup */}
            <CurrencySelectPopup
                visible={showCurrencyPopup}
                onClose={() => setShowCurrencyPopup(false)}
                onSelect={handleCurrencySelect}
                currencies={currencyList}
            />
        </PageLayout>
    )
}
